import React from 'react';
import type { Asset } from '@/types/asset';
import { ASSET_STATUS_LABELS } from '@/types/asset';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { getAssetPrimaryPhotoUrl } from '@/lib/asset-utils';
import {
  Eye,
  MapPin,
  Package,
  AlertTriangle,
} from 'lucide-react';
// format import removed - unused
import { generateAlertsForAsset, calculateAlertSummary } from '@/services/alerts-service';

// Helper function to get alert button class based on alert counts - extracted to fix nested ternary (S3358)
function getAlertButtonClass(criticalCount: number, warningCount: number): string {
  if (criticalCount > 0) {
    return "border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-950";
  }
  if (warningCount > 0) {
    return "border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950";
  }
  return "border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950";
}

interface AssetGalleryProps {
  readonly assets: Asset[];
  readonly viewMode: 'gallery' | 'list';
  readonly gridSize?: 4 | 5 | 6;
  readonly onAssetClick: (asset: Asset, mode?: 'edit' | 'view', tab?: string) => void;
  readonly selectedAssetIds?: Set<string>;
  readonly onSelectAsset?: (assetId: string, checked: boolean) => void;
}

export function AssetGallery({ assets, viewMode, gridSize = 4, onAssetClick, selectedAssetIds = new Set(), onSelectAsset }: Readonly<AssetGalleryProps>): React.ReactElement {
  const gridColsClass = {
    4: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    5: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
    6: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6',
  }[gridSize];

  if (viewMode === 'gallery') {
    return (
      <div className={cn('grid gap-4', gridColsClass)}>
        {assets.map((asset) => (
          <AssetCard 
            key={asset.id} 
            asset={asset} 
            onClick={(mode) => onAssetClick(asset, mode)}
            isSelected={selectedAssetIds.has(asset.id)}
            onSelect={onSelectAsset ? (checked) => onSelectAsset(asset.id, checked) : undefined}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-white dark:bg-black">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead className="w-[80px]">Image</TableHead>
            <TableHead>Asset Code</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Serial Number</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Acquisition Cost</TableHead>
            <TableHead className="text-right">Net Book Value</TableHead>
            <TableHead className="w-[80px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {assets.map((asset) => (
            <AssetTableRow
              key={asset.id}
              asset={asset}
              onClick={() => onAssetClick(asset)}
              isSelected={selectedAssetIds.has(asset.id)}
              onSelect={onSelectAsset ? (checked) => onSelectAsset(asset.id, checked) : undefined}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AssetCard({ asset, onClick, isSelected, onSelect }: Readonly<{ asset: Asset; onClick: (mode?: 'edit' | 'view', tab?: string) => void; isSelected?: boolean; onSelect?: (checked: boolean) => void }>) {
  const [imageError, setImageError] = React.useState(false);
  
  // Compute alert summary client-side from asset data (no API call)
  const alerts = React.useMemo(() => generateAlertsForAsset(asset), [asset]);
  const summary = React.useMemo(() => {
    const calculated = calculateAlertSummary(alerts);
    return calculated.totalCount > 0 ? calculated : null;
  }, [alerts]);

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't open asset if clicking on checkbox area
    const target = e.target as HTMLElement;
    const checkboxWrapper = target.closest('[data-checkbox-wrapper]');
    console.log('Card clicked:', { 
      target: target.tagName, 
      hasCheckboxWrapper: !!checkboxWrapper,
      targetClasses: target.className 
    });
    if (checkboxWrapper) {
      console.log('Click was on checkbox wrapper, not opening asset');
      return;
    }
    // Open asset details when clicking on the card
    console.log('Opening asset view');
    onClick('view');
  };

  const handleCheckboxChange = (checked: boolean | 'indeterminate') => {
    console.log('AssetCard checkbox change:', { assetId: asset.id, checked, isSelected, onSelectExists: !!onSelect });
    if (onSelect && typeof checked === 'boolean') {
      console.log('Calling onSelect with:', checked);
      onSelect(checked);
    } else {
      console.log('onSelect not called because:', { onSelectExists: !!onSelect, checkedType: typeof checked });
    }
  };

  const handleAlertClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open asset in view mode with alerts tab
    onClick('view', 'alerts');
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick('view');
    }
  };

  return (
    <Card 
      className={cn(
        "group cursor-pointer overflow-hidden transition-all duration-200 border border-gray-200 dark:border-gray-700",
        isSelected && "border-2 bg-green-50 dark:bg-green-950/20",
        !isSelected && "hover:border-primary/50 dark:hover:border-primary/50 hover:bg-primary/5 dark:hover:bg-primary/10"
      )}
      style={isSelected ? { borderColor: 'var(--primary)' } : undefined}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      tabIndex={0}
      aria-label={`View asset ${asset.name}`}
    >
      <div className="relative aspect-video overflow-hidden bg-muted">
        {getAssetPrimaryPhotoUrl(asset) && !imageError ? (
          <>
            <img
              src={getAssetPrimaryPhotoUrl(asset)}
              alt={asset.name}
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
              onError={handleImageError}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
            />
            {/* Dark overlay on hover for better checkbox contrast */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 pointer-events-none" />
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <Package className="h-12 w-12 text-muted-foreground" />
          </div>
        )}
        {/* Checkbox in top left - hidden when unselected until card hover */}
        <div 
          data-checkbox-wrapper
          className={cn(
            "absolute top-2 left-2 z-10 transition-opacity",
            !isSelected && "opacity-0 group-hover:opacity-100"
          )}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleCheckboxChange}
            aria-label={`Select ${asset.name}`}
            className="h-6 w-6 rounded-[5px] border cursor-pointer data-[state=unchecked]:bg-white data-[state=unchecked]:border-[#eee] dark:data-[state=unchecked]:bg-black dark:data-[state=unchecked]:border-gray-700 data-[state=checked]:bg-primary data-[state=checked]:border-primary hover:data-[state=unchecked]:bg-gray-100 hover:data-[state=unchecked]:border-gray-300 dark:hover:data-[state=unchecked]:bg-gray-900 dark:hover:data-[state=unchecked]:border-gray-600 hover:data-[state=checked]:brightness-90"
          />
        </div>
        {/* Status badge - moved to top right if status is not inService */}
        {asset.status !== 'inService' && (
          <Badge
            className={cn(
              'absolute top-2 right-2',
              asset.status === 'underMaintenance' && 'bg-yellow-500',
              asset.status === 'outOfService' && 'bg-gray-500',
              asset.status === 'disposed' && 'bg-red-500',
              asset.status === 'pending' && 'bg-blue-500',
              asset.status === 'fullyDepreciated' && 'bg-purple-500',
              asset.status === 'reserved' && 'bg-cyan-500',
              asset.status === 'lostMissing' && 'bg-orange-500',
              asset.status === 'leased' && 'bg-teal-500'
            )}
          >
            {ASSET_STATUS_LABELS[asset.status]}
          </Badge>
        )}
      </div>
      <CardContent className="p-3">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold line-clamp-1">{asset.name}</h3>
              <p className="text-xs text-muted-foreground">{asset.code}</p>
            </div>
            {summary && summary.totalCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                className={cn(
                  "h-7 px-2 gap-1 flex-shrink-0",
                  getAlertButtonClass(summary.criticalCount, summary.warningCount)
                )}
                onClick={handleAlertClick}
                title={`${summary.totalCount} alert${summary.totalCount !== 1 ? 's' : ''}`}
              >
                <AlertTriangle className="h-3 w-3" />
                <span className="text-xs font-semibold">{summary.totalCount}</span>
              </Button>
            )}
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center rounded bg-[#F5B8B8]/30 dark:bg-[#F5B8B8]/10">
                <Package className="h-3 w-3 text-[#D88888] dark:text-[#F5B8B8]" />
              </div>
              <span className="text-xs text-muted-foreground truncate">{asset.category}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center rounded bg-[#B4A7D6]/30 dark:bg-[#B4A7D6]/10">
                <MapPin className="h-3 w-3 text-[#8B7AB8] dark:text-[#B4A7D6]" />
              </div>
              <span className="text-xs text-muted-foreground truncate">{asset.location}</span>
            </div>
          </div>
          <div className="flex items-center justify-between border-t pt-2">
            <div>
              <p className="text-[10px] text-muted-foreground">NBV</p>
              <p className="text-xs font-semibold">
                {asset.currency} {asset.netBookValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground">Acquisition</p>
              <p className="text-xs">
                {asset.currency} {asset.acquisitionCost.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
          {asset.serialNumber && (
            <p className="text-[10px] text-muted-foreground truncate">
              S/N: {asset.serialNumber}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AssetTableRow({ asset, onClick, isSelected, onSelect }: { asset: Asset; onClick: () => void; isSelected?: boolean; onSelect?: (checked: boolean) => void }) {
  const [imageError, setImageError] = React.useState(false);

  const handleRowClick = () => {
    if (onSelect) {
      onSelect(!isSelected);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <TableRow
      className={cn(
        "cursor-pointer",
        isSelected && "bg-green-50 dark:bg-green-950/20"
      )}
      data-state={isSelected ? "selected" : undefined}
      onClick={handleRowClick}
    >
      <TableCell onClick={handleCheckboxClick}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelect && onSelect(!!checked)}
          aria-label={`Select ${asset.name}`}
        />
      </TableCell>
      <TableCell>
        <div className="h-12 w-12 overflow-hidden rounded bg-muted">
          {getAssetPrimaryPhotoUrl(asset) && !imageError ? (
            <img
              src={getAssetPrimaryPhotoUrl(asset)}
              alt={asset.name}
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
              onError={handleImageError}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <button
          onClick={handleLinkClick}
          className="text-primary hover:text-primary/80 font-medium hover:underline transition-colors cursor-pointer"
        >
                  {asset.code}
                </button>
      </TableCell>
      <TableCell>
        <button
          onClick={handleLinkClick}
          className="text-primary hover:text-primary/80 font-medium hover:underline transition-colors cursor-pointer"
        >
          {asset.name}
        </button>
      </TableCell>
      <TableCell>{asset.category}</TableCell>
      <TableCell>{asset.location}</TableCell>
      <TableCell className="text-muted-foreground">
        {asset.serialNumber || '-'}
      </TableCell>
      <TableCell>
        {asset.status !== 'inService' ? (
          <Badge
            variant="outline"
            className={cn(
              asset.status === 'underMaintenance' && 'border-yellow-500 text-yellow-500',
              asset.status === 'outOfService' && 'border-gray-500 text-gray-500',
              asset.status === 'disposed' && 'border-red-500 text-red-500',
              asset.status === 'pending' && 'border-blue-500 text-blue-500',
              asset.status === 'fullyDepreciated' && 'border-purple-500 text-purple-500',
              asset.status === 'reserved' && 'border-cyan-500 text-cyan-500',
              asset.status === 'lostMissing' && 'border-orange-500 text-orange-500',
              asset.status === 'leased' && 'border-teal-500 text-teal-500'
            )}
          >
            {ASSET_STATUS_LABELS[asset.status]}
          </Badge>
        ) : (
          <span className="text-muted-foreground">In Service</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="font-medium">
          {asset.currency} {asset.acquisitionCost.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="font-medium">
          {asset.currency} {asset.netBookValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
        </div>
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleEditClick}
          className="h-7 w-7"
          title="View/Edit Asset"
        >
          <Eye className="h-2.5 w-2.5" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
